import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClipboardList, Package, Cog, ArrowLeft, AlertTriangle, X, Wrench, UserCheck } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useSubscription } from "@/context/SubscriptionContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductionAccess = () => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await axios.get(`${API}/maintenance/alerts`);
        const dueAlerts = res.data.alerts || [];
        if (dueAlerts.length > 0 && !dismissed) {
          setMaintenanceAlerts(dueAlerts);
          setShowAlert(true);
        }
      } catch {
        // Silently fail - alerts are non-critical
      }
    };
    fetchAlerts();
  }, [dismissed]);

  const handleDismissAlert = () => {
    setShowAlert(false);
    setDismissed(true);
  };

  return (
    <>
      {/* Full-screen Maintenance Alert Overlay */}
      {showAlert && maintenanceAlerts.length > 0 && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm" data-testid="maintenance-alert-overlay">
          <div className="w-full max-w-2xl mx-4 relative">
            {/* Close button */}
            <Button
              onClick={handleDismissAlert}
              variant="ghost"
              className="absolute -top-12 right-0 text-gray-400 hover:text-white z-10"
              data-testid="dismiss-maintenance-alert"
            >
              <X className="w-6 h-6 mr-1" /> Close
            </Button>

            <div className="bg-gradient-to-b from-red-950 to-gray-950 rounded-2xl border-2 border-red-600/60 shadow-2xl shadow-red-900/30 overflow-hidden">
              {/* Alert Header */}
              <div className="bg-red-900/50 px-6 py-5 flex items-center gap-3 border-b border-red-700/40">
                <div className="p-2 rounded-full bg-red-600/30 animate-pulse">
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-red-300 tracking-wide">MAINTENANCE ALERT</h2>
                  <p className="text-red-400/70 text-sm">
                    {maintenanceAlerts.length} machine{maintenanceAlerts.length > 1 ? "s" : ""} require{maintenanceAlerts.length === 1 ? "s" : ""} immediate service
                  </p>
                </div>
              </div>

              {/* Alert Body */}
              <div className="p-6 space-y-3 max-h-[50vh] overflow-y-auto">
                {maintenanceAlerts.map((alert) => (
                  <div key={alert.schedule_id}
                    className="flex items-center justify-between bg-black/40 rounded-xl px-4 py-3 border border-red-800/30 hover:border-red-600/50 transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <Wrench className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-white font-semibold">{alert.machine_name}</p>
                        <p className="text-sm text-gray-400">
                          {alert.status === "overdue"
                            ? <span className="text-red-400">{alert.days_overdue} day{alert.days_overdue !== 1 ? "s" : ""} overdue</span>
                            : <span className="text-amber-400">Service due today</span>
                          }
                          {" "}&middot; Scheduled: {alert.next_service_date}
                        </p>
                        {alert.notes && (
                          <p className="text-amber-300 text-sm mt-1.5 bg-amber-900/30 px-2.5 py-1.5 rounded-md border border-amber-700/30">
                            {alert.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {alert.assigned_to ? (
                        <span className="text-emerald-400 text-sm flex items-center gap-1">
                          <UserCheck className="w-4 h-4" /> {alert.assigned_to}
                        </span>
                      ) : (
                        <span className="text-red-400/60 text-xs px-2 py-1 rounded bg-red-900/30">Unassigned</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Alert Footer */}
              <div className="px-6 py-4 bg-black/30 border-t border-red-900/30 flex justify-between items-center">
                <p className="text-gray-500 text-xs">Contact management to assign maintenance personnel</p>
                <Button
                  onClick={handleDismissAlert}
                  className="bg-red-800 hover:bg-red-700 text-white"
                  data-testid="acknowledge-alert-btn"
                >
                  Acknowledge & Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)" }}>
      {/* Animated background pattern */}
      <div className="absolute inset-0 hex-pattern opacity-30"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/5 to-transparent"></div>
      
      <div className="w-full max-w-4xl relative z-10">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-0 left-0 flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors font-bold uppercase tracking-wider text-sm"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Home
        </button>

        {/* Logo Header */}
        <div className="flex justify-center mb-4 sm:mb-8 mt-8 relative">
          <Logo size="xl" showText={true} />
          <div className="absolute right-0 top-0">
            <ThemeToggle />
          </div>
        </div>
        
        {/* Header */}
        <div className="text-center mb-6 sm:mb-12">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-blue-400 mb-2 sm:mb-4 tracking-wider">
            PRODUCTION
          </h1>
          <p className="text-sm sm:text-lg text-blue-200 uppercase tracking-widest font-bold">
            Production Management Portal
          </p>
        </div>

        {/* Cards - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Work Order Card */}
          <Link to="/workorder" data-testid="production-workorder-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-blue-500/30 shadow-lg">
              <CardContent className="p-3 sm:p-6 text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-2 sm:mb-4">
                  <ClipboardList className="h-5 w-5 sm:h-8 sm:w-8 text-white" />
                </div>
                <h2 className="text-sm sm:text-xl font-black text-blue-400 mb-1 sm:mb-3 uppercase tracking-wide">
                  Work Order
                </h2>
                <p className="text-gray-300 text-[10px] sm:text-sm mb-2 sm:mb-4 font-medium hidden sm:block">
                  Create and manage work orders, track progress.
                </p>
                <div className="inline-flex items-center text-blue-400 font-bold text-[10px] sm:text-sm uppercase tracking-wider hover:text-blue-300 transition-colors">
                  Open WO
                  <svg className="ml-1 w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Store Card */}
          <Link to="/store/dashboard" data-testid="production-store-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-blue-500/30 shadow-lg">
              <CardContent className="p-3 sm:p-6 text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-2 sm:mb-4">
                  <Package className="h-5 w-5 sm:h-8 sm:w-8 text-white" />
                </div>
                <h2 className="text-sm sm:text-xl font-black text-blue-400 mb-1 sm:mb-3 uppercase tracking-wide">
                  Store
                </h2>
                <p className="text-gray-300 text-[10px] sm:text-sm mb-2 sm:mb-4 font-medium hidden sm:block">
                  Manage inventory, materials and stock levels.
                </p>
                <div className="inline-flex items-center text-blue-400 font-bold text-[10px] sm:text-sm uppercase tracking-wider hover:text-blue-300 transition-colors">
                  Open Store
                  <svg className="ml-1 w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* POM Access Card — Pro only */}
          {hasFeature("spd") && (
          <Link to="/spd/dashboard" data-testid="production-pom-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-blue-500/30 shadow-lg">
              <CardContent className="p-3 sm:p-6 text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-2 sm:mb-4">
                  <Cog className="h-5 w-5 sm:h-8 sm:w-8 text-white" />
                </div>
                <h2 className="text-sm sm:text-xl font-black text-blue-400 mb-1 sm:mb-3 uppercase tracking-wide">
                  POM
                </h2>
                <p className="text-gray-300 text-[10px] sm:text-sm mb-2 sm:mb-4 font-medium hidden sm:block">
                  Purchase Order Management.
                </p>
                <div className="inline-flex items-center text-blue-400 font-bold text-[10px] sm:text-sm uppercase tracking-wider hover:text-blue-300 transition-colors">
                  Open POM
                  <svg className="ml-1 w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default ProductionAccess;
